/**
 * Keeps the amount inside the add-to-cart button in sync with the selected
 * variant.
 *
 * On a variant change product-info-bento.js replaces only <variant-picker> and the
 * price (`Price-<section>`). The button node itself is never re-rendered from
 * the server, and updateButton() rewrites just `[name="add"]
 * span.m-add-to-cart--text` -- an amount placed inside that span would be
 * wiped on the first change. Hence a sibling span, updated here from the
 * variantChange event emitted right after.
 *
 * Visibility is not handled here: `.m-add-to-cart[disabled]` hides the price in
 * CSS, which covers the no-variant case too (no event is emitted for it).
 */
(() => {
  // The tag lives inside the buy_buttons block, so a page with two product
  // sections parses it twice and would subscribe twice.
  if (window.LeonartoAtcPriceBound) return;
  window.LeonartoAtcPriceBound = true;

  const PRICE_SELECTOR = '[data-atc-price]';

  /* Cents to text in the shop's money format (`LeonartoSettings.money_format`,
     snippets/theme-data.liquid, from `shop.money_format`) — the same format
     `| money` renders the first amount with. This store's is
     "{{amount_with_comma_separator}} zł". Ported from the vendor helper in
     theme-global.js (removed 2026-09-25): the same four placeholders; an unknown one now falls
     back to `amount` (the vendor printed nothing) and a format without a
     placeholder is returned as is (the vendor threw). */
  const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  const withDelimiters = (cents, precision, thousands = ',', decimal = '.') => {
    if (cents == null || isNaN(cents)) return '0';
    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fraction ? decimal + fraction : '');
  };

  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const template = format || '${{amount}}';
    const match = template.match(PLACEHOLDER);
    if (!match) return template;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return template.replace(PLACEHOLDER, value);
  };

  // Scoped to the <product-info> that emitted the event, and deliberately
  // without a document-wide fallback: a page can carry several product forms
  // (upsell, complementary products, a second featured product), and falling
  // back to the whole document would stamp one product's price onto another's
  // button. No match means this event is not ours -- do nothing.
  const scopeFor = (sectionId) =>
    document.querySelector(
      `product-info[data-section-id="${sectionId}"], product-info[data-original-section="${sectionId}"]`
    );

  const onVariantChange = (event) => {
    const variant = event?.data?.variant;
    if (!variant) return;

    const scope = scopeFor(event.data.sectionId);
    if (!scope) return;

    const money = formatMoney(variant.price, window.LeonartoSettings.money_format);

    scope.querySelectorAll(PRICE_SELECTOR).forEach((price) => {
      const amount = price.querySelector('[data-atc-price-amount]');
      if (amount) amount.innerHTML = money;
    });
  };

  window.LeonartoEvents.subscribe(window.LeonartoTheme.pubSubEvents.variantChange, onVariantChange);
})();
